import { FormikErrors, FormikTouched } from 'formik';

import { Button, ErrorMessage, TWO_FA_INPUTS_COUNT } from '@libs/components';
import { getPhoneLastDigits } from '@libs/helpers';

import { CodeInputs } from 'src/components';
import { TwoFactorAuthType } from 'src/constants';
import { useTranslation } from 'src/hooks';

import {
  ActionWrapper,
  CodeWrapper,
  ErrorWrapper,
  Description,
  PhoneForm,
  CustomDescription,
  CustomPhoneInput,
} from './styles';

type GenericForm<T> = {
  phone: T;
};
type ChangePhoneFormProps = {
  values: GenericForm<string>;
  touched: FormikTouched<GenericForm<boolean>>;
  formErrors: FormikErrors<GenericForm<string>>;
  onCancel?: () => void;
  code: string;
  isSameNumber?: boolean;
  phoneToDisplay: string;
  isValid: boolean;
  handleSubmit: () => void;
  handleChange: (e: React.ChangeEvent) => void;
  sendCode: (phone?: string) => void;
  setCode: (code: string) => void;
  clearCode: (code: string) => void;
  errorMessage?: string;
  countDown: number;
  isCounterRunning: boolean;
  initialValue?: string;
  handlePhoneChange: (vals: { phone: string; code: string }) => void;
  isButtonActive: boolean;
  counterInterval: (val?: number | undefined) => void;
  shouldRender: boolean;
};

const ChangePhoneForm: React.FC<ChangePhoneFormProps> = ({
  // onCancel,
  code,
  formErrors,
  touched,
  isSameNumber,
  phoneToDisplay,
  isValid,
  handleSubmit,
  values,
  handleChange,
  sendCode,
  setCode,
  clearCode,
  errorMessage,
  countDown,
  isCounterRunning,
  handlePhoneChange,
  initialValue,
  isButtonActive,
  shouldRender,
}) => {
  const { t } = useTranslation();

  const isActionDisabled =
    !isCounterRunning || code.length !== TWO_FA_INPUTS_COUNT;

  const phoneFormError = touched.phone ? t(formErrors.phone) : '';
  const phoneErrorMessage = isSameNumber
    ? t('phone_change_same_number_error')
    : phoneFormError;

  const phoneButton = isCounterRunning ? `${countDown}` : t('input_button');

  const description = !isCounterRunning
    ? t('phone_change_force_input_info')
    : t('phone_change_force_input_info_second', {
        lastDigits: getPhoneLastDigits(phoneToDisplay),
      });

  const customSubmitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!!code && isValid) {
      handleSubmit();
    }
  };

  const phoneEmailButtonWithText = isCounterRunning
    ? t('register_resend_code', { seconds: countDown })
    : t('register_resend');

  const handleSendCode = () => {
    sendCode(values.phone);
  };

  const handleComplete = (code: string) => {
    handlePhoneChange({ phone: values.phone, code });
  };

  const onComplete = shouldRender ? handleComplete : setCode;

  return (
    <PhoneForm onSubmit={customSubmitHandler}>
      {!shouldRender && (
        <CustomPhoneInput
          sendCode={phoneButton}
          valid={isButtonActive}
          name="phone"
          value={values.phone}
          onChange={handleChange}
          placeholder="5xx-xxx-xxx"
          errorMessage={phoneErrorMessage}
          onSendCodeClick={handleSendCode}
          sendCodeText={t('input_button')}
          label={t('phone_change_new_number')}
          disabled={isCounterRunning}
        />
      )}
      {!shouldRender ? (
        <Description description={description} />
      ) : (
        <CustomDescription
          description={t('phone_change_force_input_info_second', {
            lastDigits: getPhoneLastDigits(initialValue),
          })}
        />
      )}
      <CodeWrapper>
        <CodeInputs
          onComplete={onComplete}
          onCodeDelete={clearCode}
          type={TwoFactorAuthType.SMS}
        />
      </CodeWrapper>
      <ErrorWrapper>
        <ErrorMessage
          errorMessage={t(errorMessage)}
          errorTestId="phoneErrorMessage"
        />
      </ErrorWrapper>
      <ActionWrapper>
        {!shouldRender ? (
          <Button
            type="submit"
            title={t('common_save')}
            disabled={isActionDisabled}
            testId="submitButton"
          />
        ) : (
          <Button
            onClick={handleSendCode}
            title={phoneEmailButtonWithText}
            disabled={isCounterRunning}
          />
        )}
      </ActionWrapper>
    </PhoneForm>
  );
};
export default ChangePhoneForm;
